import axios from 'axios'
import {
  ListResponse,
  SimpleResponse,
} from '../interfaces/Shipment/ListResponseDto'
import {
  businessUnitMapper,
  mapBUDtoToBUInterface,
  mapBusinessUnitDtoListToBusinessUnitInterface,
  storeDjangoToBUInterface,
} from '../utils/mappers'
import {
  Nullable,
  BusinessUnitDTO,
  BusinessUnitInterface,
  ServiceDTO,
  PaymentModeId,
  DeliveryEnum,
  StoreDjangoDto,
} from '../interfaces'
import { BusinessUnitDTOV2 } from '../interfaces/BusinessUnit/BusinessUnitInterface'
import { apiDjango } from './ApiDjango'

const { REACT_APP_URL_PATH_API, REACT_APP_URL_PATH_API_DJANGO } = process.env

// Carga masiva (API Django). GET /api/store — listado de tiendas. Lanza en caso de fallo
// para que el thunk del sub-slice lo capture. Nota: el DTO Django no trae buid ni buTypeID;
// dedupe/filtra por buCode aguas abajo (ver businessUnitOptions en ShipmentBulkUpload).
export const getStoresDjango = async (
  params?: Record<string, unknown>
): Promise<BusinessUnitInterface[]> => {
  try {
    const resp = await apiDjango.get(`${REACT_APP_URL_PATH_API_DJANGO}/store`, {
      params,
    })
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const list = (resp.data.data ?? []) as StoreDjangoDto[]
      return list.map(storeDjangoToBUInterface)
    }
    console.error('getStoresDjango: respuesta con error', {
      code: resp.data?.code,
      message: resp.data?.message,
    })
    throw new Error(resp.data?.message || 'No se pudieron obtener las tiendas')
  } catch (error) {
    console.error('getStoresDjango: fallo', { error })
    throw error
  }
}

// Carga masiva (API Django). GET /api/store/coverage?latitude=&longitude= — tienda que
// cubre unas coordenadas. Drop-in de getBusinessUnitByTerritory: misma firma
// (longitude, latitude) y mismo retorno SimpleResponse<BusinessUnitInterface>. NO lanza.
// `signal` permite abortar (Review dispara hasta 200 llamadas; cancelar en cleanup).
export const getStoreByCoverageDjango = async (
  longitude: number,
  latitude: number,
  signal?: AbortSignal
): Promise<SimpleResponse<BusinessUnitInterface>> => {
  try {
    const resp = await apiDjango.get(
      `${REACT_APP_URL_PATH_API_DJANGO}/store/coverage`,
      { params: { latitude, longitude }, signal }
    )
    if (resp.status === 200 && resp.data && resp.data.error === false) {
      const store = resp.data.data as StoreDjangoDto | null
      return {
        model: store ? storeDjangoToBUInterface(store) : null,
        didError: false,
        message: resp.data.message ?? '',
        errorMessage: '',
      }
    }
    const errMessage =
      resp.data?.message ||
      resp.data?.error_data?.error ||
      'No se pudo obtener la tienda de cobertura del punto especificado'
    return {
      model: null,
      didError: true,
      message: errMessage,
      errorMessage: errMessage,
    }
  } catch (error) {
    // Aborto de la peticion (cleanup del useEffect): no es un error de negocio.
    if ((error as any)?.name === 'CanceledError' || (error as any)?.code === 'ERR_CANCELED') {
      return {
        model: null,
        didError: true,
        message: 'Consulta de cobertura cancelada',
        errorMessage: 'Consulta de cobertura cancelada',
      }
    }
    console.error('getStoreByCoverageDjango: fallo', { longitude, latitude, error })
    return {
      model: null,
      didError: true,
      message: 'No se pudo obtener la tienda de cobertura del punto especificado',
      errorMessage:
        'No se pudo obtener la tienda de cobertura del punto especificado',
    }
  }
}

export const getBusinessUnits = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var datamap = storeDto?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap
    }
  } catch (error) {}
  return null
}

export const getBusinessUnitsType = async (
  type: number
): Promise<Nullable<BusinessUnitInterface[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var stoteDtoBu = storeDto as BusinessUnitDTOV2[]
      stoteDtoBu = stoteDtoBu.filter(
        (x) => x.buTypeID?.toString() === type.toString()
      )
      var datamap = stoteDtoBu?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap
    }
  } catch (error) {}
  return null
}

export const getBusinessUnitsPolly = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&IsNational=false&withPolygonsList=true&excludeOperationLocked=true`
  try {
    const response = await axios.get(uri)
    if (response.status === 200) {
      const storeDto = response.data
      var datamap = storeDto?.map((dto: BusinessUnitDTO) =>
        businessUnitMapper(dto)
      )
      return datamap.filter((bu: BusinessUnitInterface) => bu.buCode != '8110')
    }
  } catch (error) {}

  return null
}

export const getBusinessUnitByTerritory = async (
  longitude: number,
  latitude: number
): Promise<SimpleResponse<BusinessUnitInterface>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetBusinessUnitByTerritory?longitude=${longitude}&latitude=${latitude}`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      const storeDto = response.data.model
      if (storeDto != null) {
        response.data.model = mapBUDtoToBUInterface(storeDto)
      }
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudo obtener la tienda de cobertura del punto especificado',
    errorMessage:
      'No se pudo obtener la tienda de cobertura del punto especificado',
  }
}

export const getBusinessUnitsInnovus = async (): Promise<
  Nullable<BusinessUnitInterface[]>
> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/BusinessUnitV2?status=1&withPolygonsList=false&IsNational=false&includePlatforms=true`
  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      const storeDto = response.data
      return mapBusinessUnitDtoListToBusinessUnitInterface(storeDto)
    }
  } catch (error) {}

  return null
}

export const getServicesFromPaymentMode = async (
  paymentModeID: PaymentModeId
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/getServicesFromPaymentMode?PaymentModeID=${paymentModeID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios',
    errorMessage: 'No se pudieron obtener los servicios',
  }
}

export const getServicesFromDeliveryType = async (
  deliveryTypeID: DeliveryEnum
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetServicesFromDeliveryType?DeliveryTypeID=${deliveryTypeID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios',
    errorMessage: 'No se pudieron obtener los servicios',
  }
}

export const getLockedServicesByTier = async (
  tierID: number
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetLockedServicesByTier?TierID=${tierID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getLockBUServices = async (
  buCode: string
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetLockBUServices?BUCode=${buCode}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getAppLockedServices = async (
  applicationID: number
): Promise<ListResponse<ServiceDTO[]>> => {
  const uri = `${REACT_APP_URL_PATH_API}/BusinessUnit/GetAppLockedServices?ApplicationID=${applicationID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudieron obtener los servicios bloqueados',
    errorMessage: 'No se pudieron obtener los servicios bloqueados',
  }
}

export const getService = async (
  serviceID: number
): Promise<ListResponse<ServiceDTO>> => {
  const uri = `${REACT_APP_URL_PATH_API}/GetService?ServiceID=${serviceID}`

  try {
    const response = await axios.get(uri)

    if (response.status === 200) {
      return response.data
    }
  } catch (error) {}

  return {
    model: null,
    didError: true,
    message: 'No se pudo obtener el servicio',
    errorMessage: 'No se pudo obtener el servicio',
  }
}
